import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { formatDateVN } from '../../utils/formatDate';
import './lessonLibrary.css';

export default function LessonDetail() {
    const { lessonId, week } = useParams();
    const wkNum = Number(week || 0);
    const { user } = useAuth();
    const apiUrl = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();

    const resolveThumbnailSrc = (thumb) => {
        if (!thumb) return '';
        try {
            if (String(thumb).startsWith('data:')) return thumb;
            if (String(thumb).startsWith('http://') || String(thumb).startsWith('https://')) return thumb;
            if (String(thumb).startsWith('/')) {
                const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
                return base + thumb;
            }
            return thumb;
        } catch (e) { return thumb; }
    };

    const [lesson, setLesson] = useState(null);
    const [lessonsList, setLessonsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSummary, setShowSummary] = useState(false);
    const [showTopic, setShowTopic] = useState(false);
    const [showItems, setShowItems] = useState(false);
    const [progressPercent, setProgressPercent] = useState(0);
    const [lessonsLeft, setLessonsLeft] = useState(35);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 520 : false);
    const [attendanceExistsByWeek, setAttendanceExistsByWeek] = useState({});

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                if (!apiUrl || !token) return;
                const res = await fetch(`${apiUrl}/api/lessons`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) return;
                const jd = await res.json();
                const list = jd.lessons || [];
                if (mounted) setLessonsList(list || []);
                const L = list.find(x => String(x.id) === String(lessonId));
                if (mounted) setLesson(L || null);
                // compute progress for this lesson's grade (reuse logic from LessonLibrary)
                try {
                    const totalLessons = 35;
                    const publishedSet = new Set();
                    const lessonsForGrade = L && L.grade_id ? (list || []).filter(x => Number(x.grade_id) === Number(L.grade_id)) : [];
                    for (const ll of lessonsForGrade) {
                        const contentWeeks = Array.isArray(ll.content) ? ll.content : [];
                        const rawActivities = ll.activities || ll.activity || ll.activity_list || ll.activity_json || null;
                        let activityWeeks = [];
                        if (Array.isArray(rawActivities)) activityWeeks = rawActivities;
                        else if (typeof rawActivities === 'string' && rawActivities.trim()) {
                            try { activityWeeks = JSON.parse(rawActivities); } catch (e) { activityWeeks = []; }
                        }
                        for (let i = 0; i < contentWeeks.length; i++) {
                            const cw = contentWeeks[i];
                            const n = (cw && (cw.week !== undefined && cw.week !== null)) ? Number(cw.week) : (i + 1);
                            if (n) publishedSet.add(Number(n));
                        }
                        for (const a of activityWeeks) {
                            if (!a) continue;
                            if (a.week !== undefined && a.week !== null) publishedSet.add(Number(a.week));
                        }
                    }
                    const publishedWeeks = publishedSet.size;
                    const left = Math.max(0, totalLessons - publishedWeeks);
                    const pct = Math.min(100, Math.round((publishedWeeks / totalLessons) * 100));
                    if (mounted) {
                        setLessonsLeft(left);
                        setProgressPercent(pct);
                    }
                } catch (e) { console.error('Failed to compute progress in LessonDetail', e); }
            } catch (e) { console.error(e); }
            if (mounted) setLoading(false);
        })();
        return () => { mounted = false; };
    }, [apiUrl, lessonId]);

    // detect mobile width so we can hide some large UI for small screens (student view)
    useEffect(() => {
        function onResize() {
            try { setIsMobile(window.innerWidth <= 520); } catch (e) { /* ignore */ }
        }
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // For Students: fetch attendance existence per week for student's class (and for teacher/mods later)
    useEffect(() => {
        let mounted = true;
        async function loadAttendance() {
            try {
                if (!user || (user.role || '').toLowerCase() !== 'student') {
                    if (mounted) setAttendanceExistsByWeek({});
                    return;
                }
                if (!apiUrl || !user.class_id) { if (mounted) setAttendanceExistsByWeek({}); return; }
                const token = localStorage.getItem('token');
                if (!token) { if (mounted) setAttendanceExistsByWeek({}); return; }
                const res = await fetch(`${apiUrl}/api/classes/${user.class_id}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) { if (mounted) setAttendanceExistsByWeek({}); return; }
                const jd = await res.json();
                const weeks = jd.weeks || [];
                const exists = {};
                for (const w of weeks) {
                    const recs = Array.isArray(w.records) ? w.records : (Array.isArray(w.students) ? w.students : (Array.isArray(w.records || []) ? w.records : []));
                    const wkKey = String(w.week || w.week_number || w.weekNo || w.weekNo || '');
                    exists[wkKey] = Array.isArray(recs) && recs.length > 0;
                }
                if (mounted) setAttendanceExistsByWeek(exists);
            } catch (e) { console.error('Failed to load attendance for student in LessonDetail', e); if (mounted) setAttendanceExistsByWeek({}); }
        }
        loadAttendance();
        return () => { mounted = false; };
    }, [apiUrl, user && user.role, user && user.class_id]);

    // For teachers/mods: fetch attendance existence for the lesson's grade (find a class)
    useEffect(() => {
        let mounted = true;
        async function loadForTeacher() {
            try {
                const roleLower = (user && user.role || '').toLowerCase();
                if (!user || (roleLower !== 'teacher' && roleLower !== 'mod')) return;
                if (!apiUrl || !lesson || !lesson.grade_id) return;
                const token = localStorage.getItem('token');
                if (!token) return;
                const cres = await fetch(`${apiUrl}/api/classes`, { headers: { Authorization: `Bearer ${token}` } });
                if (!cres.ok) return;
                const cd = await cres.json();
                const classes = cd.classes || [];
                let classMatch = null;
                if (roleLower === 'teacher') {
                    classMatch = classes.find(c => String(c.teacher_username) === String(user.username) && Number(c.grade_id) === Number(lesson.grade_id));
                }
                if (!classMatch) {
                    classMatch = classes.find(c => Number(c.grade_id) === Number(lesson.grade_id));
                }
                if (!classMatch || !classMatch.id) return;
                const classId = classMatch.id;
                const res = await fetch(`${apiUrl}/api/classes/${classId}/attendance/weeks`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) return;
                const jd = await res.json();
                const weeks = jd.weeks || [];
                const exists = {};
                for (const w of weeks) {
                    const recs = Array.isArray(w.records) ? w.records : (Array.isArray(w.students) ? w.students : (Array.isArray(w.records || []) ? w.records : []));
                    const wkKey = String(w.week || w.week_number || w.weekNo || w.weekNo || '');
                    exists[wkKey] = Array.isArray(recs) && recs.length > 0;
                }
                if (mounted) setAttendanceExistsByWeek(exists);
            } catch (e) { console.error('Failed to load attendance for teacher/mod in LessonDetail', e); }
        }
        loadForTeacher();
        return () => { mounted = false; };
    }, [apiUrl, user && user.role, lesson && lesson.grade_id]);

    if (loading) return <div className="page-view"><div className="card">Đang tải...</div></div>;
    if (!lesson) return <div className="page-view"><div className="card">Không tìm thấy bài giảng</div></div>;

    // build weekNumbers for this lesson (respect status flag)
    const contentWeeks = Array.isArray(lesson.content) ? lesson.content : [];
    const rawActivities = lesson.activities || lesson.activity || lesson.activity_list || lesson.activity_json || null;
    let activityWeeks = [];
    if (Array.isArray(rawActivities)) activityWeeks = rawActivities;
    else if (typeof rawActivities === 'string' && rawActivities.trim()) {
        try { activityWeeks = JSON.parse(rawActivities); } catch (e) { activityWeeks = []; }
    }
    const nums = new Set();
    for (let i = 0; i < contentWeeks.length; i++) {
        const cw = contentWeeks[i];
        const itemStatus = (cw && (cw.status === undefined || cw.status === null)) ? 'active' : (cw && cw.status ? cw.status : 'active');
        const status = (lesson && lesson.status === 'inactive') ? 'inactive' : itemStatus;
        if (status === 'inactive') continue;
        const n = (cw && (cw.week !== undefined && cw.week !== null)) ? Number(cw.week) : (i + 1);
        if (n) nums.add(Number(n));
    }
    for (const a of activityWeeks) {
        const itemStatus = (a && (a.status === undefined || a.status === null)) ? 'active' : (a && a.status ? a.status : 'active');
        const status = (lesson && lesson.status === 'inactive') ? 'inactive' : itemStatus;
        if (status === 'inactive') continue;
        if (a && a.week !== undefined && a.week !== null) nums.add(Number(a.week));
    }
    if (!nums.size && (contentWeeks.length || activityWeeks.length)) nums.add(1);
    const weekArr = Array.from(nums).sort((a, b) => a - b);

    // compute activeCards for this lesson's grade (reuse logic from LessonLibrary)
    const lessonsForGrade = (lessonsList || []).filter(l => Number(l.grade_id) === Number(lesson && lesson.grade_id));
    const activeCards = [];
    const inactiveCards = [];
    try {
        const weekMap = new Map();
        for (const l of (lessonsForGrade || [])) {
            const contentWeeks = Array.isArray(l.content) ? l.content : [];
            for (let i = 0; i < contentWeeks.length; i++) {
                const cw = contentWeeks[i];
                const wk = (cw && (cw.week !== undefined && cw.week !== null)) ? Number(cw.week) : (i + 1);
                if (!wk) continue;
                const itemStatus = (cw && (cw.status === undefined || cw.status === null)) ? 'active' : (cw.status || 'active');
                const status = (l && l.status === 'inactive') ? 'inactive' : itemStatus;
                if (!weekMap.has(wk)) weekMap.set(wk, []);
                weekMap.get(wk).push({ lesson: l, kind: 'content', item: cw, status });
            }
            const rawActivities = l.activities || l.activity || l.activity_list || l.activity_json || null;
            let activityWeeks = [];
            if (Array.isArray(rawActivities)) activityWeeks = rawActivities;
            else if (typeof rawActivities === 'string' && rawActivities.trim()) {
                try { activityWeeks = JSON.parse(rawActivities); } catch (e) { activityWeeks = []; }
            }
            for (const a of (activityWeeks || [])) {
                const wk = (a && (a.week !== undefined && a.week !== null)) ? Number(a.week) : null;
                if (!wk) continue;
                const itemStatus = (a && (a.status === undefined || a.status === null)) ? 'active' : (a.status || 'active');
                const status = (l && l.status === 'inactive') ? 'inactive' : itemStatus;
                if (!weekMap.has(wk)) weekMap.set(wk, []);
                weekMap.get(wk).push({ lesson: l, kind: 'activity', item: a, status });
            }
        }
        const weeks = Array.from(weekMap.keys()).sort((a, b) => a - b);
        for (const wk of weeks) {
            const occ = weekMap.get(wk) || [];
            const activeOcc = occ.find(o => o.status !== 'inactive');
            if (activeOcc) activeCards.push({ wk, lesson: activeOcc.lesson, base: activeOcc.item });
            else {
                const first = occ[0] || null;
                if (first) inactiveCards.push({ wk, lesson: first.lesson, base: first.item });
            }
        }
    } catch (e) { console.error('Failed to compute week cards in LessonDetail', e); }

    // find earliest active missing attendance
    const activeMissingAttendance = (activeCards || []).filter(a => {
        const wkKey = String(a.wk);
        return !(attendanceExistsByWeek && attendanceExistsByWeek[wkKey]);
    }).sort((x, y) => Number(x.wk) - Number(y.wk));
    const earliestMissing = (activeMissingAttendance && activeMissingAttendance.length) ? activeMissingAttendance[0] : null;

    if (!weekArr.includes(wkNum)) {
        return (
            <div className="page-view">
                <div className="card">
                    <div>Tuần {wkNum} không khả dụng hoặc đã bị ẩn.</div>
                    <div style={{ marginTop: 12 }}>
                        <button onClick={() => navigate(-1)}>Quay lại</button>
                    </div>
                </div>
            </div>
        );
    }

    // pick base and activity for wkNum
    let base = (contentWeeks || []).find(c => (c && (c.week !== undefined && c.week !== null) && Number(c.week) === Number(wkNum))) || null;
    if (!base && contentWeeks && contentWeeks.length >= wkNum) base = contentWeeks[wkNum - 1];
    const actByWeek = {};
    for (const a of activityWeeks) if (a && a.week !== undefined && a.week !== null) actByWeek[String(a.week)] = a;
    const rawAct = actByWeek[String(wkNum)] || {};
    const title = (base && base.title) || '';
    const canva_link = (base && (base.canva_link || base.slide)) || '';
    const summary = (base && base.summary) || '';

    // normalize topic: try several common locations and fallbacks
    const topic = (
        (rawAct && (
            rawAct.topic || rawAct.title || rawAct.name || (rawAct.activity && rawAct.activity.topic) || rawAct.topic_name || rawAct.topicName
        )) || (base && (base.topic || base.title)) || ''
    );

    // normalize items: accept array, comma-separated string, nested activity.items, or JSON string
    function normalizeItems(v) {
        if (!v && v !== 0) return null;
        if (Array.isArray(v)) return v;
        if (typeof v === 'string') {
            const trimmed = v.trim();
            // try parse JSON array
            if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || trimmed.startsWith('"')) {
                try { const parsed = JSON.parse(trimmed); if (Array.isArray(parsed)) return parsed; } catch (e) { /* not JSON */ }
            }
            // fallback: ONLY split by newline. Do not split on commas.
            let parts = [];
            if (/\r?\n/.test(trimmed)) {
                parts = trimmed.split(/\r?\n/);
            } else {
                // preserve whole string as single item when no newline
                parts = [trimmed];
            }
            parts = parts.map(s => s.trim()).filter(Boolean);
            return parts.length ? parts : null;
        }
        if (typeof v === 'object') {
            // maybe it's { items: [...] } or { activity_items: [...] }
            if (Array.isArray(v.items)) return v.items;
            if (Array.isArray(v.activity_items)) return v.activity_items;
            // if object keys are numeric
            const arr = Object.keys(v).map(k => v[k]).filter(x => typeof x === 'string' || typeof x === 'number');
            if (arr.length) return arr;
        }
        return null;
    }

    const items = normalizeItems(rawAct.items)
        || normalizeItems(rawAct.activity && rawAct.activity.items)
        || normalizeItems(rawAct.activity_items)
        || normalizeItems(rawAct.activity_items_list)
        || normalizeItems(rawAct.content)
        || normalizeItems(base && base.items)
        || normalizeItems(base && base.activity_items)
        || null;

    // normalize outline similar to items (allow array, comma/newline separated string, or nested)
    const outline = normalizeItems(rawAct.outline)
        || normalizeItems(rawAct.activity && rawAct.activity.outline)
        || normalizeItems(rawAct.activity_outline)
        || normalizeItems(rawAct.topic)
        || normalizeItems(rawAct.activity && rawAct.activity.topic)
        || normalizeItems(base && base.outline)
        || normalizeItems(base && base.activity_outline)
        || normalizeItems(base && base.topic)
        || null;

    // render outline with fallback: arrays -> list, strings -> split by comma/newline into list when appropriate
    function renderOutline(o) {
        if (!o) return <div>{topic || 'No information'}</div>;
        if (Array.isArray(o)) return <ul className="act-item-list">{o.map((it, i) => <li key={i}>{it}</li>)}</ul>;
        if (typeof o === 'string') {
            // split only on newline, do not split on comma
            const parts = o.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
            if (parts.length > 1) return <ul className="act-item-list">{parts.map((it, i) => <li key={i}>{it}</li>)}</ul>;
            return <div>{o}</div>;
        }
        return <div>{String(o)}</div>;
    }

    return (
        <div className="page-view">
            {/* <button className="back" onClick={() => navigate(-1)}>← Back</button> */}
            {/* Hide the big progress bar for students on small screens; render progress + earliest missing thumbnail on desktop */}
            {!(user && String(user.role).toLowerCase() === 'student' && isMobile) && (
                <div className="lesson-progress-bar" style={{ maxWidth: 1280, margin: '0 auto 16px' }}>
                    <div className='lesson-progress-thumbnail'>
                        {(!isMobile && earliestMissing) ? (
                            <div className="missing-attendance-thumbs">
                                {(() => {
                                    const it = earliestMissing;
                                    const l = it.lesson; const base = it.base || {};
                                    const thumb = (base && base.thumbnail) || (base && (base.canva_link || base.slide)) || '';
                                    const lessonTitle = (base && base.title) || (l && l.title) || `Week ${it.wk}`;
                                    return (
                                        <button key={`miss-${l ? l.id : 'x'}-${it.wk}`} className="missing-attendance-thumb" onClick={() => l && navigate(`/lessons/${l.id}/week/${it.wk}`)} title={`Week ${it.wk} — ${lessonTitle}`}>
                                            {thumb ? (
                                                <img src={resolveThumbnailSrc(thumb)} alt={lessonTitle} />
                                            ) : (
                                                <div className="missing-thumb-placeholder">Week {it.wk}</div>
                                            )}
                                        </button>
                                    );
                                })()}
                            </div>
                        ) : null}
                    </div>
                    <div className='lesson-progress-content'>
                        <div className="progress-title">Teaching Progress</div>
                        <div className="progress-bar-outer">
                            <div className="progress-bar-inner" style={{ width: progressPercent + '%' }}></div>
                        </div>
                        <div className="progress-label">{lessonsLeft} lessons left</div>
                    </div>

                </div>
            )}
            <main className="lesson-main three-card" style={{ maxWidth: 1280, margin: '0 auto' }}>
                <div className="card card-title">
                    <div>
                        <span className="lesson-title">{title || '—'}</span>
                        {canva_link && (
                            <div className="month-card-iframe-wrapper"><iframe src={canva_link} allowFullScreen loading="lazy" className="month-card-iframe" /></div>
                        )}
                    </div>
                </div>
                <div className="card card-summary">
                    <h4 style={{ cursor: 'pointer', fontSize: 16 }} onClick={() => setShowSummary(s => !s)}>{showSummary ? '▼' : '▶'} Summary</h4>
                    {showSummary && (
                        <div>
                            {(() => {
                                const s = summary;
                                if (!s) return <span>—</span>;
                                // show summary as plain text (preserve newlines)
                                if (Array.isArray(s)) return <div className="summary-text" style={{ whiteSpace: 'pre-wrap' }}>{s.join('\n')}</div>;
                                return <div className="summary-text" style={{ whiteSpace: 'pre-wrap' }}>{s}</div>;
                            })()}
                        </div>
                    )}

                    <div style={{ marginTop: 12 }}>
                        <h4 style={{ cursor: 'pointer', fontSize: 16 }} onClick={() => setShowTopic(s => !s)}>{showTopic ? '▼' : '▶'} Outline</h4>
                        <div className={`activity-block ${showTopic ? '' : 'collapsed'}`}>
                            {showTopic && (
                                <div className="act-item-panel">
                                    {renderOutline(outline)}
                                </div>
                            )}

                            <h4 style={{ marginTop: 10, cursor: 'pointer', fontSize: 16 }} onClick={() => setShowItems(s => !s)}>{showItems ? '▼' : '▶'} Prepared supplies</h4>
                            {showItems && (
                                <div className="act-item-panel">
                                    {Array.isArray(items) ? (
                                        <ul className="act-item-list">
                                            {items.map((it, i) => <li key={i}>{it}</li>)}
                                        </ul>
                                    ) : items ? (
                                        <div>{items}</div>
                                    ) : (
                                        <div className="muted">No activities available</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
